import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, User } from 'lucide-react';
import { useAdmin } from '../../contexts/AdminContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

export const AdminLogin = () => {
  const { login } = useAdmin();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
      });

      const { access_token, role, username: adminUsername } = response.data;
      
      login({ username: adminUsername, role }, access_token);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Admin login error:', error);
      const errorMessage = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4">
      <Card className="w-full max-w-md p-8 bg-white/95 backdrop-blur-sm border-slate-200">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_id-selfservice/artifacts/tfwsm7zn_Logo%2BNuevo%2BMi-Token.png.webp"
              alt="Mi-Token Logo"
              className="h-16 w-auto"
            />
          </div>
          <h1 className="font-outfit font-bold text-3xl text-slate-900 mb-2">Admin Portal</h1>
          <p className="text-slate-600 font-ibm">Mi-Token Administration</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="username" className="font-ibm font-medium text-slate-700">
              Username
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="pl-10 h-12 font-ibm"
                placeholder="Enter admin username"
                data-testid="admin-username-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="font-ibm font-medium text-slate-700">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pl-10 h-12 font-ibm"
                placeholder="Enter password"
                data-testid="admin-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-12 font-ibm font-medium bg-slate-900 hover:bg-slate-800"
            disabled={loading}
            data-testid="admin-login-button"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-sm text-slate-600 hover:text-slate-900 font-ibm"
          >
            ← Back to User Portal
          </a>
        </div>
      </Card>
    </div>
  );
};
